import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { delta, fmtDays, fmtHours } from "./format";

const Delta = ({ d }) => {
  if (!d) return <span className="font-mono-eo text-[10px] text-[#B8B5AA]">no prior data</span>;
  const color = d.good === null ? "#8A8A80" : d.good ? "#2A9D8F" : "#E63946";
  const Icon = d.pct >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-0.5 font-mono-eo text-[11px]" style={{ color }}>
      <Icon size={12} /> {Math.abs(d.pct)}% <span className="text-[#8A8A80] ml-1">vs prev</span>
    </span>
  );
};

export function KpiStrip({ k }) {
  const items = [
    { id: "involved", label: "Files involved", value: k.involved, hint: "all time" },
    { id: "open", label: "Open files", value: k.open, hint: "in circulation" },
    { id: "created", label: "Created", value: k.created, d: delta(k.created, k.created_prev) },
    { id: "closed", label: "Closed", value: k.closed, d: delta(k.closed, k.closed_prev) },
    { id: "turnaround", label: "Avg turnaround", value: fmtDays(k.avg_turnaround_days), d: delta(k.avg_turnaround_days, k.avg_turnaround_days_prev, true) },
    { id: "stuck", label: "Stuck > 7 days", value: k.stuck, hint: "idle with holder", alert: k.stuck > 0 },
    { id: "waiting", label: "Waiting with me", value: k.waiting_with_me, hint: "needs your action", accent: true },
    { id: "response", label: "My avg response", value: fmtHours(k.my_avg_response_hours), d: delta(k.my_avg_response_hours, k.my_avg_response_hours_prev, true) },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 border border-[#DCD9D0] bg-white" data-testid="kpi-strip">
      {items.map((it, i) => (
        <motion.div
          key={it.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.04, duration: 0.3 }}
          data-testid={`kpi-${it.id}`}
          className={`relative p-5 border-[#DCD9D0] border-b xl:border-b-0 ${i % 4 !== 0 ? "md:border-l" : ""} ${i % 2 !== 0 ? "border-l md:border-l" : ""} xl:border-l ${i === 0 ? "xl:border-l-0" : ""} transition-colors duration-150 hover:bg-[#FAFAF7]`}
        >
          {(it.alert || it.accent) && <span className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: it.alert ? "#E63946" : "#1D3557" }} />}
          <div className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">{it.label}</div>
          <div data-testid={`kpi-${it.id}-value`} className="font-heading text-[30px] font-bold leading-none mt-3" style={{ color: it.alert ? "#E63946" : "#0A0A0A" }}>{it.value ?? "—"}</div>
          <div className="mt-2 min-h-[16px]">{it.hint ? <span className="font-mono-eo text-[10px] text-[#8A8A80]">{it.hint}</span> : <Delta d={it.d} />}</div>
        </motion.div>
      ))}
    </div>
  );
}
